import React, { useEffect } from 'react';
import useScrollLock from '../hooks/useScrollLock';
import { X, CheckCircle2, AlertCircle, Info, AlertTriangle } from 'lucide-react';

interface AlertModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  message: string;
  type?: 'success' | 'error' | 'info' | 'warning';
}

const AlertModal: React.FC<AlertModalProps> = ({
  open,
  onClose,
  title,
  message,
  type = 'info',
}) => {
  // Handle scroll locking
  useScrollLock(open);

  // Close on Escape key


  const getTypeStyles = () => {
    switch (type) {
      case 'success':
        return {
          iconBg: 'bg-green-50',
          iconColor: 'text-green-600',
          iconBorder: 'border-green-200',
          titleColor: 'text-gray-900',
          buttonClass: 'bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white shadow-lg shadow-green-200',
          icon: CheckCircle2,
          gradient: 'from-green-500/20 to-emerald-500/20',
        };
      case 'error':
        return {
          iconBg: 'bg-red-50',
          iconColor: 'text-red-600',
          iconBorder: 'border-red-200',
          titleColor: 'text-gray-900',
          buttonClass: 'bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white shadow-lg shadow-orange-200',
          icon: AlertCircle,
          gradient: 'from-red-500/20 to-orange-500/20',
        };
      case 'warning':
        return {
          iconBg: 'bg-orange-50',
          iconColor: 'text-orange-600',
          iconBorder: 'border-orange-200',
          titleColor: 'text-gray-900',
          buttonClass: 'bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white shadow-lg shadow-orange-200',
          icon: AlertTriangle,
          gradient: 'from-orange-500/20 to-orange-600/20',
        };
      default:
        return {
          iconBg: 'bg-orange-50',
          iconColor: 'text-orange-600',
          iconBorder: 'border-orange-200',
          titleColor: 'text-gray-900',
          buttonClass: 'bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white shadow-lg shadow-orange-200',
          icon: Info,
          gradient: 'from-orange-500/20 to-orange-600/20',
        };
    }
  };

  const styles = getTypeStyles();
  const IconComponent = styles.icon;
  const [isAnimating, setIsAnimating] = React.useState(false);

  useEffect(() => {
    if (open) {
      // Trigger animation after mount
      setTimeout(() => setIsAnimating(true), 10);
    } else {
      setIsAnimating(false);
    }
  }, [open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop with blur */}
      <div
        className={`fixed inset-0 bg-black/40 backdrop-blur-sm transition-opacity duration-300 ${isAnimating ? 'opacity-100' : 'opacity-0'
          }`}
        onClick={onClose}
      />

      {/* Modal */}
      <div
        className={`relative bg-[#FFF8F0] rounded-2xl p-0 max-w-md w-full border border-orange-200/60 shadow-lg transform transition-all duration-300 ${isAnimating
          ? 'opacity-100 scale-100 translate-y-0'
          : 'opacity-0 scale-95 translate-y-4'
          }`}
      >
        {/* Decorative gradient bar at top */}
        <div className={`h-1 bg-gradient-to-r ${styles.gradient} rounded-t-2xl`} />

        <div className="p-6">
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute right-4 top-4 text-gray-400 hover:text-gray-600 transition-all rounded-lg p-1.5 hover:bg-orange-50 hover:scale-110"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>

          {/* Icon and Content */}
          <div className="flex flex-col items-center text-center pt-2 pb-4">
            {/* Icon with animated background */}
            <div
              className={`relative mb-4 ${styles.iconBg} ${styles.iconBorder} border-2 rounded-full p-4 transition-all duration-500 ${isAnimating ? 'scale-100 rotate-0' : 'scale-0 rotate-180'
                }`}
            >
              <IconComponent className={`relative ${styles.iconColor} w-8 h-8`} strokeWidth={2.5} />
            </div>

            {/* Title */}
            <h2
              className={`${styles.titleColor} text-2xl font-medium mb-2 tracking-tight transition-all duration-500 delay-100 ${isAnimating ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'
                }`}
            >
              {title}
            </h2>

            {/* Message */}
            <p
              className={`text-gray-600 text-sm leading-relaxed max-w-sm transition-all duration-500 delay-200 ${isAnimating ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'
                }`}
            >
              {message}
            </p>
          </div>

          {/* Footer */}
          <div
            className={`flex justify-center pt-2 transition-all duration-500 delay-300 ${isAnimating ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'
              }`}
          >
            <button
              onClick={onClose}
              className={`${styles.buttonClass} px-8 py-3 rounded-xl font-medium transition-all duration-300 hover:scale-105 active:scale-95`}
            >
              Got it
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AlertModal;

